import Stack from '@mui/material/Stack'

import { H3, Text } from '@mxenabled/mxui'
import { observer } from 'mobx-react-lite'

import { useActionContext, useInsightsV2Store } from '../../common'
import type { Beat, DrilldownPayload } from '../../core'

import { createVariantBlock } from '../blocks/createVariantBlock'
import { type ActionBlockProps } from '../mappings/blocks'

// This block doubles as the reference example for the two building blocks a real
// action block uses:
//   1. useActionContext<T>() — read the drawer's opaque, block-specific context.
//   2. createVariantBlock    — dispatch to a per-variant component with a fallback.
// Register a block with variants exactly like `PlaceholderVariants` below, then
// wire it into src/insights-v2/mappings/blocks.ts.

interface PlaceholderVariantProps {
  beat: Beat
}

/** Default variant — also the fallback for unknown/absent variants.
 *  Wrapped in `observer` because it reads the drawer's observable context via
 *  useActionContext; without it a memoized ancestor would stop it re-rendering.
 *  This is the copy-paste template for real blocks, so it must model the rule. */
const DefaultPlaceholder = observer(({ beat }: PlaceholderVariantProps) => {
  // Chart drilldowns put { chartBarIndex, tabIndex } on the drawer context; a
  // CTA open carries none, so default to 0.
  const { chartBarIndex = 0, tabIndex = 0 } = useActionContext<DrilldownPayload>() ?? {}

  return (
    <Stack sx={{ alignItems: 'center', gap: 8, p: 24, textAlign: 'center' }}>
      <H3>Action Block Layer Drilldown</H3>
      <Text>
        This is a placeholder action block. Replace it with your own implementation by registering
        your block in src/insights-v2/mappings/blocks.ts.
      </Text>
      <Text>Beat: {beat.guid}</Text>
      <Text>
        Drilldown: chartBarIndex {chartBarIndex}, tabIndex {tabIndex}
      </Text>
    </Stack>
  )
})

/** A second sample variant, to show registry dispatch with more than one entry. */
const CompactPlaceholder = ({ beat }: PlaceholderVariantProps) => (
  <Stack sx={{ alignItems: 'center', gap: 8, p: 24, textAlign: 'center' }}>
    <H3>Action Block Layer Drilldown (compact)</H3>
    <Text>Beat: {beat.guid}</Text>
  </Stack>
)

// Register one component per backend `variant`; unknown/absent variants fall
// back to DefaultPlaceholder.
const PlaceholderVariants = createVariantBlock<PlaceholderVariantProps>({
  variants: {
    default: DefaultPlaceholder,
    compact: CompactPlaceholder,
  },
  fallback: DefaultPlaceholder,
  name: 'Placeholder',
})

export const PlaceholderActionBlock = observer(function PlaceholderActionBlock({
  beat,
}: ActionBlockProps) {
  const { actionDrawer } = useInsightsV2Store()
  const cta =
    actionDrawer.trigger === 'secondaryCta'
      ? beat.insights_payload?.secondary_cta
      : beat.insights_payload?.primary_cta

  return <PlaceholderVariants beat={beat} variant={cta?.variant} />
})
